import { useState } from 'react';

// eslint-disable-next-line no-shadow
export enum SelectionMode {
  Single = 'single',
  All = 'all',
}

type DataRowIdResolver<T extends { [key: string]: unknown }> = (
  row: T,
) => string | number;

function defaultDataRowIdResolver(row: {
  /* eslint-disable  @typescript-eslint/no-explicit-any */
  [key: string]: any;
}): string | number {
  if ('id' in row) {
    return row.id;
  }

  throw new Error(
    'Your data row does not directly contain an `id`. Pass a `dataRowIdResolver` to `useDataTableState`',
  );
}

export const useDataTableState = <T extends { [key: string]: unknown }>(
  data: T[],
  {
    selectedRows: initialSelectedRows = [],
    allRowsSelected: initialAllRowsSelected = false,
    dataRowIdResolver = defaultDataRowIdResolver,
  }: {
    selectedRows?: (string | number)[];
    allRowsSelected?: boolean;
    dataRowIdResolver?: DataRowIdResolver<T>;
  } = {
    selectedRows: [],
    allRowsSelected: false,
    dataRowIdResolver: defaultDataRowIdResolver,
  },
) => {
  const [selectedRows, setSelectedRows] =
    useState<(string | number)[]>(initialSelectedRows);

  const [allRowsSelected, setAllRowsSelected] = useState<boolean>(
    initialAllRowsSelected,
  );

  const handleSelectionChange = (
    id: string | number,
    isSelected: boolean,
    selectionType: SelectionMode,
  ) => {
    if (selectionType === SelectionMode.All) {
      setAllRowsSelected(isSelected);
    } else if (allRowsSelected) {
      setAllRowsSelected(false);
    }

    if (selectionType === SelectionMode.Single) {
      setSelectedRows((prevSelected) =>
        isSelected
          ? [...prevSelected, id]
          : prevSelected.filter((rowId) => rowId !== id),
      );
    } else if (selectionType === SelectionMode.All) {
      const RowsSelected = data.map(dataRowIdResolver) ?? [];
      setSelectedRows(RowsSelected);
    }
  };

  const clearSelection = () => {
    setSelectedRows([]);
    setAllRowsSelected(false);
  };

  return {
    selectedRows,
    allRowsSelected,
    handleSelectionChange,
    clearSelection,
  };
};
